import { useCallback, useEffect, useState } from 'react';
import galleryImagePlaceholder from '@/assets/gallery-image-placeholder.svg?url';
import type { MappedGalleries } from '@/api/mappers/gallery.mapper';
import { reportService } from '@/api/services/report.service';
import type {
  PhotoGalleryImageState,
  PhotoGalleryItem,
  PhotoGalleryVariant,
} from '@/components/PhotoGallery/PhotoGallery.types';
import {
  PublicGalleryVariantResolver,
  type GalleryVariantState,
} from './public-gallery-variant-resolver';

const getDisplaySource = (state: GalleryVariantState): string => {
  if (state.kind === 'ready' || state.kind === 'legacy-fallback') {
    return state.src;
  }

  return state.kind === 'placeholder' ? galleryImagePlaceholder : '';
};

const toPhotoState = (
  state: GalleryVariantState,
): PhotoGalleryImageState => state.kind;

const unresolvedState: GalleryVariantState = {
  kind: 'loading',
  usesDirectUrl: false,
};

export const usePublicGalleryImageResolver = (
  galleries: MappedGalleries | undefined,
  token: string | undefined,
  reportScope: string | undefined,
): MappedGalleries | undefined => {
  const [, setVersion] = useState(0);
  const [resolverState, setResolverState] = useState<{
    resolver: PublicGalleryVariantResolver;
    scope: string;
  }>();
  const fetchBatch = useCallback(
    async (
      items: Parameters<typeof reportService.getPublicGalleryVariantUrls>[0],
    ) => {
      const response = await reportService.getPublicGalleryVariantUrls(items, token);
      return response.data;
    },
    [token],
  );
  const resolverScope = `${reportScope ?? ''}:${token ?? ''}`;
  const resolver =
    resolverState?.scope === resolverScope
      ? resolverState.resolver
      : undefined;

  useEffect(() => {
    const nextResolver = new PublicGalleryVariantResolver({
      fetchBatch,
      scopeKey: resolverScope,
    });
    const unsubscribe = nextResolver.subscribe(() =>
      setVersion((version) => version + 1),
    );

    setResolverState({ resolver: nextResolver, scope: resolverScope });

    return () => {
      unsubscribe();
      nextResolver.dispose();
    };
  }, [fetchBatch, resolverScope]);

  if (!galleries) {
    return undefined;
  }

  const hydratePhoto = (photo: PhotoGalleryItem): PhotoGalleryItem => {
    if (!photo.attachmentId) {
      return photo;
    }

    const thumbnail =
      resolver?.getState(photo.attachmentId, 'THUMBNAIL') ?? unresolvedState;
    const large =
      resolver?.getState(photo.attachmentId, 'LARGE') ?? unresolvedState;
    const thumbnailSrc = getDisplaySource(thumbnail);
    const largeSrc = getDisplaySource(large) || thumbnailSrc;

    return {
      ...photo,
      fullSizeState: toPhotoState(large),
      fullSrc: largeSrc,
      refreshVariant: resolver
        ? (variant: PhotoGalleryVariant) =>
            resolver.refreshDirect(photo.attachmentId!, variant)
        : undefined,
      requestFullSize: resolver
        ? () =>
            resolver.request({
              attachmentId: photo.attachmentId!,
              legacySrc: photo.fullSrc || undefined,
              size: 'LARGE',
            })
        : undefined,
      requestThumbnail: resolver
        ? () =>
            resolver.request({
              attachmentId: photo.attachmentId!,
              legacySrc: photo.src || undefined,
              size: 'THUMBNAIL',
            })
        : undefined,
      src: thumbnailSrc,
      thumbnailState: toPhotoState(thumbnail),
    };
  };

  const mainPhoto = galleries.mainPhoto
    ? hydratePhoto(galleries.mainPhoto)
    : undefined;

  return {
    ...galleries,
    all: galleries.all.map(hydratePhoto),
    body: galleries.body.map(hydratePhoto),
    diagnostics: galleries.diagnostics.map(hydratePhoto),
    documentation: galleries.documentation.map(hydratePhoto),
    engineCompartment: galleries.engineCompartment.map(hydratePhoto),
    interior: galleries.interior.map(hydratePhoto),
    mainPhoto,
    mainPhotoUrl: mainPhoto ? mainPhoto.src : galleries.mainPhotoUrl,
    other: galleries.other.map(hydratePhoto),
    undercarriage: galleries.undercarriage.map(hydratePhoto),
  };
};
